import { toast } from "react-toastify";

import Brand from "./models/Brand";
import BrandRequest from "./models/BrandRequest";
// eslint-disable-next-line no-unused-vars
import BrandRequestBrand from "./models/BrandRequestBrand";
import LogoVersion from "./models/LogoVersion";
import Industry from "./models/Industry";
import { post, get, put } from "axios";
import { BRAND_REQUEST_GROUP_STATUSES } from "./enums";

const { industry: industries } = require("./industries.json");
const _get = require("lodash/get");
const config = {
  withCredentials: true
};

const graphUrl = "graph.visua.com";
const apiUrl = "api.visua.com";
const dataUrl = "data.visua.com";

/**
 * Converts logVer_1 to 1 if needed
 * @param {string} id
 */
export function id(id) {
  if (id.indexOf("_") !== -1) {
    return id.split("_")[1];
  } else {
    return id;
  }
}

export default class DataService {
  /**
   *
   * @param {BrandRequest[]} data
   */
  static mapGraphData(data) {
    return data.map(row => {
      const rawIndustry = _get(row, "brandRequestBrand[0].brand.industry", {});
      const industry = new Industry({
        id: rawIndustry.id,
        name: rawIndustry.description
      });

      const logoVersions = _get(
        row,
        "brandRequestBrand[0].brand.logoVersion",
        []
      ).map(({ id, thumbUrl }) => {
        return new LogoVersion({ id, thumbUrl });
      });

      const isInLibrary = _get(row, "brandRequestBrand.length") > 0;

      const sortBrandRequestBrand = (a, b) => {
        return (
          b.comparisonMethod.localeCompare(a.comparisonMethod) ||
          b.comparisonAccuracy - a.comparisonAccuracy
        );
      };

      /** @type Brand[] */
      const brands = row.brandRequestBrand
        .sort(sortBrandRequestBrand)
        .map(brb => {
          // Fix in case brand is now deleted
          if (!brb.brand) {
            return;
          }
          return new Brand(brb.brand);
        });
      brands.unshift(new Brand({ name: "New" }));

      let exampleUrls;
      try {
        exampleUrls = JSON.parse(row.exampleUrls);
      } catch (error) {
        console.error(
          `Couldn't parse example_urls for "${row.name}" (${row.id}).`,
          error
        );
      }

      return new BrandRequest({
        id: row.id,
        name: row.name,
        contentWebsite: row.contentWebsite,
        status: row.status,
        industry,
        countryId: row.geoCountryId,
        industryId: row.industryId,
        logoVersions,
        isInLibrary,
        brands,
        logoVersionRequests: row.logoVersionRequest,
        meta: row.meta,
        note: row.note,
        exampleUrls,
        brandRequestBrands: row.brandRequestBrand
      });
    });
  }

  static getIndustries() {
    return industries.sort(function(a, b) {
      var nameA = a.description.toUpperCase();
      var nameB = b.description.toUpperCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    }).map(
      industry => new Industry({ id: industry.id, name: industry.description })
    );
  }

  static getCountries() {
    const url = `//${graphUrl}/geoCountry?version=2&fields=id,name`;

    return get(url, config).then(response => response.data.geoCountry);
  }

  /**
   *
   * @param {File} file
   * @param {string} brandRequestGroupId
   */
  static uploadBrandsCsv(file, brandRequestGroupId) {
    const url = `//${apiUrl}/brandRequestGroups/${id(
      brandRequestGroupId
    )}/uploadCsv`;

    const formData = new FormData();

    formData.append("csv", file);

    return post(url, formData, config);
  }

  /**
   *
   * @param {string} id - brareqgro_xxxx
   */
  static getBrandRequestGroup(id) {
    const url = `//${graphUrl}/${id}?fields=id,name,detectionSettingId,status,dateEntered&version=2`;

    return get(url, config).then(response => response.data.brandRequestGroup);
  }

  static getId(id) {
    const url = `//${graphUrl}/${id}?version=2`;

    return get(url, config).then(response => response.data);
  }

  /**
   * @typedef {object} gbrgParam
   * @prop {import("./enums").BRAND_REQUEST_GROUP_STATUSES[]} status
   * @prop {string} fields
   * @prop {number} [limit]
   * @prop {number} [offset]
   * @prop {string} [sortBy]
   * @prop {string} [name]
   * @prop {'asc'|'desc'} [sortDir]
   * @returns {Promise<{ brandRequestGroups: BrandRequestGroup[], count: number }>}
   */
  static getBrandRequestGroups(
    /** @type {gbrgParam} */ {
      status,
      fields,
      limit,
      offset,
      sortBy,
      sortDir,
      name
    }
  ) {
    const limitQuery = limit ? `.limit(${limit})` : "";
    const offsetQuery = offset ? `.offset(${offset})` : "";
    const url =
      `//${graphUrl}/brandRequestGroup.in(status,${status.join(",")})${
        name ? '.like(name,"' + name + '")' : ""
      }.${sortDir || "desc"}(${sortBy ||
        "dateEntered"})${offsetQuery}${limitQuery}?version=2` +
      (fields ? `&fields=${fields}` : "");

    const countUrl = `//${graphUrl}/brandRequestGroup.in(status,${status.join(
      ","
    )})${name ? '.like(name,"' + name + '")' : ""}?version=2&agg=count`;

    return Promise.all([get(url, config), get(countUrl, config)]).then(
      ([response, countResponse]) => {
        return {
          brandRequestGroups: response.data.brandRequestGroup,
          count: countResponse.data.brandRequestGroup.count
        };
      }
    );
  }

  static get BRAND_REQUEST_FIELDS() {
    return (
      `id,name,contentWebsite,status,industryId,geoCountryId,exampleUrls,meta,note,` +
      `logoVersionRequest(id,brandId,logoVersionId,status,thumbUrl),` +
      `brandRequestBrand(id,status,comparisonAccuracy,comparisonMethod,brand(id,name,industry(id,description),` +
      `logoVersion(id,thumbUrl),` +
      `logo(id,name,geoCountryId,logoContent(id,website))` +
      `))`
    );
  }

  /**
   *
   * @param {string} brandRequestId - brareq_xx
   * @returns {Promise<BrandRequest>}
   */
  static getBrandRequest(brandRequestId) {
    const url =
      `//${graphUrl}/${brandRequestId}` +
      `?fields=${this.BRAND_REQUEST_FIELDS}&version=2`;

    return get(url, config).then(
      response => this.mapGraphData([response.data.brandRequest])[0]
    );
  }

  /**
   *
   * @param {string} brandRequestGroupId
   * @returns {Promise<BrandRequest[]>}
   */
  static getBrandRequests(brandRequestGroupId) {
    const url =
      `//${graphUrl}/` +
      `brandrequest.eq(brandRequestGroupId,${
        brandRequestGroupId.split("_")[1]
      })?` +
      `fields=${this.BRAND_REQUEST_FIELDS}&version=2`;

    return get(url, config).then(response =>
      this.mapGraphData(response.data.brandRequest)
    );
  }

  /**
   *
   * @param {*} brandRequestId
   * @param {Partial<BrandRequest>} data
   */
  static updateBrandRequest(brandRequestId, data) {
    const url = `//${graphUrl}/${brandRequestId}?version=2`;
    const {
      status,
      name,
      contentWebsite,
      industryId,
      countryId,
      meta,
      note
    } = data;

    var params = new URLSearchParams();
    status !== undefined && params.append("status", status.toString());
    name && params.append("name", name);
    contentWebsite && params.append("contentWebsite", contentWebsite);
    industryId && params.append("industryId", id(industryId));
    countryId && params.append("geoCountryId", id(countryId));
    note && params.append("note", note);
    meta && params.append("meta", JSON.stringify(meta));

    return put(url, params, config);
  }

  static updateId(id, data) {
    const url = `//${graphUrl}/${id}?version=2`;
    var params = new URLSearchParams();

    Object.keys(data).forEach(
      key => data[key] && params.append(key, data[key])
    );

    return put(url, params, config);
  }

  static createBrandRequestGroup(name, description, developerId) {
    const url = `//${graphUrl}/BrandRequestGroup?version=2`;

    var params = new URLSearchParams();
    params.append("name", name);
    params.append("description", description);
    params.append("developerId", developerId);
    params.append("status", "1");

    return post(url, params, config).then(
      response => response.data.brandRequestGroup.id
    );
  }

  static updateBrandRequestGroup(
    id,
    /** @type {BrandRequestGroup} */ { name, status }
  ) {
    const url = `//${graphUrl}/${id}?version=2`;

    return this.getBrandRequestGroup(id).then(brandRequestGroup => {
      if (brandRequestGroup.status === BRAND_REQUEST_GROUP_STATUSES.FINISHED) {
        toast(
          "⚠️ This batch is marked as complete. Your UI is out of sync. Page will reload automatically.",
          { type: toast.TYPE.ERROR, position: toast.POSITION.BOTTOM_RIGHT }
        );
        setTimeout(() => {
          window.location.reload(false);
        }, 5000);
      } else {
        var params = new URLSearchParams();
        name && params.append("name", name);
        status !== undefined && params.append("status", status.toString());

        return put(url, params, config);
      }
    });
  }

  /**
   *
   * @param {string} brandRequestBrandId - brareqbra_xx
   * @returns {Promise<BrandRequestBrand>}
   */
  static getBrandRequestBrand(brandRequestBrandId) {
    const url =
      `//${graphUrl}/${brandRequestBrandId}?` +
      `fields=id,status,brand(id,name,industry(id,description),` +
      `logoVersion(id,thumbUrl),` +
      `logo(id,name,geoCountryId,logoContent(id,website)))&version=2`;

    return get(url, config).then(response => response.data.brandRequestBrand);
  }

  /**
   *
   * @param {string} brandRequestId - either brareq_xx or just xx
   * @param {string} brandId - id without graph prefix
   * @returns {Promise<string>} brareqbra_xx
   */
  static createBrandRequestBrand(brandRequestId, brandId) {
    const url = `//${graphUrl}/BrandRequestBrand?version=2`;

    var params = new URLSearchParams();
    params.append("brandRequestId", id(brandRequestId));
    params.append("brandId", brandId);
    params.append("status", "-1");
    params.append("comparisonAccuracy", "1");
    params.append("comparisonMethod", "UserSelected");

    return post(url, params, config).then(
      response => response.data.brandRequestBrand.id
    );
  }

  static updateBrandRequestBrand(id, status) {
    const url = `//${graphUrl}/${id}?version=2`;

    var params = new URLSearchParams();
    params.append("status", status);

    return put(url, params, config).then(
      response => response.data.brandRequestBrand
    );
  }

  static getLogoVersionRequest(brandRequestId, logoVersionId) {
    const url =
      `//${graphUrl}/` +
      `LogoVersionRequest.eq(brandRequestId,${id(brandRequestId)})` +
      `.eq(logoVersionId,${id(logoVersionId)})?version=2`;

    return get(url, config).then(
      response =>
        response.data.logoVersionRequest && response.data.logoVersionRequest[0]
    );
  }

  static updateLogoVersionRequest(id, status) {
    const url = `//${graphUrl}/${id}?version=2`;

    var params = new URLSearchParams();
    params.append("status", status);

    return put(url, params, config).then(
      response => response.data.logoVersionRequest
    );
  }

  /**
   *
   * @typedef {object} param0
   * @prop {string} brandRequestId
   * @prop {string} brandId
   * @prop {string=} logoVersionId
   * @prop {number} status
   */
  static createLogoVersionRequest(
    /** @type {param0} */ { brandRequestId, brandId, logoVersionId, status }
  ) {
    const url = `//${graphUrl}/LogoVersionRequest?version=2`;

    var params = new URLSearchParams();
    params.append("brandRequestId", id(brandRequestId));
    brandId && params.append("brandId", id(brandId));
    logoVersionId && params.append("logoVersionId", id(logoVersionId));
    params.append("status", status);
    params.append("aspectRatio", "1");

    return post(url, params, config).then(
      response => response.data.logoVersionRequest.id
    );
  }

  /*
   * @param {imageFile} - event.target.files[0]
   */
  static uploadLogoVersionRequestImage(
    brandRequestId,
    imageFile,
    status,
    brandId
  ) {
    const url = `//${apiUrl}/brandRequests/${id(
      brandRequestId
    )}/logoVersionRequests/upload`;

    var formData = new FormData();
    formData.append("image", imageFile);
    formData.append("status", status);
    brandId && formData.append("brandId", id(brandId));

    return post(url, formData, config).then(response => response.data.data);
  }

  static getDetectionSettings() {
    const url = `//${graphUrl}/detectionSetting?fields=Deployment,Developer(Company)&version=2`;

    return get(url, config).then(response => response.data.detectionSetting);
  }

  static getDetectionSettingVisualClassGroup(detectionSettingId) {
    const url = `//${graphUrl}/detectionSettingVisualClassGroup.eq(detectionSettingId,${id(
      detectionSettingId
    )})/visualClassGroup?version=2`;

    return get(url, config).then(response => response.data.visualClassGroup);
  }

  static uploadBatch(
    brandRequestGroupId,
    visualClassGroupId,
    detectionSettingId
  ) {
    const url = `//${apiUrl}/brandRequestGroups/${id(
      brandRequestGroupId
    )}/complete`;

    return this.getBrandRequestGroup(brandRequestGroupId).then(
      brandRequestGroup => {
        if (
          brandRequestGroup.status === BRAND_REQUEST_GROUP_STATUSES.FINISHED
        ) {
          toast(
            "⚠️ This batch is marked as complete. Your UI is out of sync. Page will reload automatically.",
            { type: toast.TYPE.ERROR, position: toast.POSITION.BOTTOM_RIGHT }
          );
          // Return a promise that never resolves
          return new Promise(() => {
            setTimeout(() => {
              window.location.reload(false);
            }, 5000);
          });
        } else {
          var params = new URLSearchParams();
          params.append("visualClassGroupId", id(visualClassGroupId));
          params.append("detectionSettingId", id(detectionSettingId));

          return post(url, params, config).then(response =>
            console.log(response.data)
          );
        }
      }
    );
  }

  static getMe() {
    const url = `https://${apiUrl}/me`;

    return get(url, config).then(response => response.data.data);
  }

  static getBrandsAndLogoVersions() {
    const url = `https://${dataUrl}/logo-activation/rds/getBrandsAndLogoVersions?version=2&transpose=false`;

    return get(url, config).then(response => response.data);
  }

  static sendEmail(batchName, brandRequestGroupId) {
    const url = `//${apiUrl}/emails`;

    var params = new URLSearchParams();
    params.append(
      "subject",
      `Logo Activation batch "${batchName}" is done! 🦄`
    );
    params.append(
      "bodyHtml",
      `Link: <a href="https://www.logograb.com/backoffice/logoactivation/activate/${brandRequestGroupId}">${batchName}</a>`
    );
    params.append(
      "bodyText",
      `This is a link: https://www.logograb.com/backoffice/logoactivation/activate/${brandRequestGroupId}`
    );

    return post(url, params, config);
  }

  static logout() {
    return get("https://www.logograb.com/backoffice/logout", config);
  }
}



// WEBPACK FOOTER //
// ./src/DataService.js